import { useState, useEffect } from 'react';
import { Loader2, Download, Trash2, Image, Sparkles, Palette } from 'lucide-react';

const STYLES = [
  { id: 'auto', label: 'Auto', desc: 'AI chooses the best style' },
  { id: 'logo', label: 'Logo', desc: 'Clean vector branding' },
  { id: 'headshot', label: 'Headshot', desc: 'Professional portrait' },
  { id: 'avatar', label: 'Avatar', desc: 'Stylized character' },
  { id: 'product', label: 'Product', desc: 'Commercial photography' },
  { id: 'art', label: 'Art', desc: 'Digital illustration' },
];

export default function CreativeStudio({ apiUrl }) {
  const [prompt, setPrompt] = useState('');
  const [style, setStyle] = useState('auto');
  const [title, setTitle] = useState('');
  const [generating, setGenerating] = useState(false);
  const [preview, setPreview] = useState(null);
  const [gallery, setGallery] = useState([]);
  const [error, setError] = useState('');

  const backendUrl = process.env.REACT_APP_BACKEND_URL;

  useEffect(() => { fetchGallery(); }, []);

  const fetchGallery = async () => {
    try {
      const res = await fetch(`${apiUrl}/creative/gallery`);
      const data = await res.json();
      setGallery(data.images || []);
    } catch (e) { console.error(e); }
  };

  const generateImage = async () => {
    if (!prompt.trim() || generating) return;
    setGenerating(true);
    setError('');
    setPreview(null);

    try {
      const res = await fetch(`${apiUrl}/creative/generate-image`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ prompt, style, title: title || undefined, save_to_library: true }),
      });

      if (!res.ok) {
        const err = await res.json();
        throw new Error(err.detail || 'Generation failed');
      }

      const data = await res.json();
      setPreview(data.image_base64);
      setPrompt('');
      setTitle('');
      fetchGallery();
    } catch (e) {
      setError(e.message);
    } finally {
      setGenerating(false);
    }
  };

  const deleteImage = async (imageId) => {
    try {
      await fetch(`${apiUrl}/creative/${imageId}`, { method: 'DELETE' });
      fetchGallery();
    } catch (e) { console.error(e); }
  };

  return (
    <div className="min-h-screen bg-slate-950 text-white" data-testid="creative-studio">
      <div className="max-w-6xl mx-auto px-4 py-8">
        {/* Header */}
        <div className="mb-8">
          <div className="flex items-center gap-3 mb-2">
            <Sparkles className="w-7 h-7 text-cyan-400" />
            <h1 className="text-2xl font-bold text-white tracking-tight">Creative Studio</h1>
            <span className="text-[10px] bg-cyan-900/40 text-cyan-400 px-2 py-0.5 rounded border border-cyan-800/30">GPT IMAGE 1</span>
          </div>
          <p className="text-sm text-slate-500">Generate logos, headshots, avatars, product photos, and art for the Federation.</p>
        </div>

        {/* Generator */}
        <div className="bg-slate-900/60 border border-slate-800 rounded-xl p-5 mb-8">
          {/* Style picker */}
          <div className="flex flex-wrap gap-2 mb-4">
            {STYLES.map(s => (
              <button key={s.id} onClick={() => setStyle(s.id)}
                className={`px-3 py-1.5 rounded-md text-xs transition-colors ${
                  style === s.id
                    ? 'bg-cyan-600 text-white'
                    : 'bg-slate-800 text-slate-400 hover:text-white border border-slate-700'
                }`}
                data-testid={`style-${s.id}`}>
                {s.label}
              </button>
            ))}
          </div>

          {/* Prompt */}
          <div className="flex gap-3 mb-3">
            <input value={prompt} onChange={e => setPrompt(e.target.value)}
              onKeyDown={e => e.key === 'Enter' && generateImage()}
              placeholder="Describe what you want to create..."
              className="flex-1 bg-slate-800 border border-slate-700 rounded-lg px-4 py-2.5 text-sm text-white placeholder-slate-500 focus:outline-none focus:border-cyan-600"
              data-testid="creative-prompt" />
          </div>

          <div className="flex items-center gap-3">
            <input value={title} onChange={e => setTitle(e.target.value)}
              placeholder="Title (optional)"
              className="flex-1 bg-slate-800 border border-slate-700 rounded-lg px-4 py-2 text-sm text-white placeholder-slate-500 focus:outline-none focus:border-cyan-600" />
            <button onClick={generateImage} disabled={generating || !prompt.trim()}
              className="px-5 py-2 bg-cyan-600 hover:bg-cyan-500 disabled:opacity-40 rounded-lg text-sm font-medium flex items-center gap-2 transition-colors"
              data-testid="creative-generate">
              {generating ? <Loader2 className="w-4 h-4 animate-spin" /> : <Sparkles className="w-4 h-4" />}
              {generating ? 'Generating...' : 'Generate'}
            </button>
          </div>

          {error && <p className="mt-3 text-red-400 text-xs">{error}</p>}

          {/* Preview */}
          {preview && (
            <div className="mt-5 flex justify-center">
              <div className="relative group">
                <img src={`data:image/png;base64,${preview}`} alt="Generated"
                  className="max-h-[400px] rounded-lg border border-slate-700 shadow-xl" />
                <a href={`data:image/png;base64,${preview}`} download="generated.png"
                  className="absolute bottom-2 right-2 bg-black/60 text-white p-2 rounded-lg opacity-0 group-hover:opacity-100 transition-opacity">
                  <Download className="w-4 h-4" />
                </a>
              </div>
            </div>
          )}

          {generating && (
            <div className="mt-5 flex flex-col items-center gap-3 py-8">
              <Loader2 className="w-8 h-8 text-cyan-400 animate-spin" />
              <p className="text-sm text-slate-400">Creating your image... this may take up to 60 seconds.</p>
            </div>
          )}
        </div>

        {/* Gallery */}
        <div className="mb-4 flex items-center gap-2">
          <Palette className="w-5 h-5 text-cyan-400" />
          <h2 className="text-lg font-semibold text-white">Gallery</h2>
          <span className="text-xs text-slate-500">{gallery.length} images</span>
        </div>

        {gallery.length === 0 ? (
          <div className="text-center py-16 text-slate-600">
            <Image className="w-12 h-12 mx-auto mb-3 opacity-30" />
            <p className="text-sm">No images generated yet. Create your first above.</p>
          </div>
        ) : (
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4">
            {gallery.map(img => (
              <div key={img.id} className="group relative bg-slate-900 rounded-lg border border-slate-800 overflow-hidden">
                <img src={`${backendUrl}/api/uploads/${img.file_path}`} alt={img.title}
                  className="w-full aspect-square object-cover" loading="lazy" />
                <div className="absolute inset-0 bg-gradient-to-t from-black/80 to-transparent opacity-0 group-hover:opacity-100 transition-opacity flex flex-col justify-end p-3">
                  <p className="text-xs text-white font-medium truncate">{img.title}</p>
                  <p className="text-[10px] text-slate-400">{(img.file_size / 1024).toFixed(0)} KB</p>
                  <button onClick={() => deleteImage(img.id)}
                    className="absolute top-2 right-2 bg-red-500/80 hover:bg-red-500 p-1 rounded text-white">
                    <Trash2 className="w-3 h-3" />
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
